import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import * as XLSX from "xlsx";
import { useHistory } from "react-router-dom";
import {
  ArrowBack,
  ArrowForward,
  CancelOutlined,
  CheckCircleOutline,
  CurrencyRupee,
  DescriptionOutlined,
  FileDownloadOutlined,
  FileUploadOutlined,
  Replay,
  Search,
  StorageOutlined,
} from "@mui/icons-material";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { detailCardSx } from "components/DesignSystem/RequestDetailField";
import { globalTextFieldSx, sharedTableBodyCellSx } from "components/DesignSystem/sharedUiPatterns";
import { PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import {
  StatCard,
  designColors,
  designRadii,
  designShell,
  designType,
  designWeights,
  paginationFooter,
  rowsPerPageOptions,
} from "services/constants/designTokens";
import { tooltipStyleProps } from "services/constants/Styles";
import { showToast } from "services/utils/Status";
import { checkPayInputImport, downloadPayInputTemplate, savePayInputImport } from "pages/PayRoll/Shared/reimbursementApi";
import { convertDateTimeCells } from "../Shared/importMapping";
import { WizardStepper } from "../Shared/WizardStepper";
import { SelectField } from "../Shared/fieldInputs";
import { formatDate, money } from "./reimbursementPayUi";

// Payroll > Pay Inputs > Reimbursements > Import: Upload File -> Map Columns
// -> Preview & Import, same layout as the Penalties / Leave / Shift Earnings
// imports. The file is read in the browser only to map its columns; Map
// Columns > Next rebuilds it with the template's column names and sends it to
// POST payroll/pay-input/reimbursement/import/?dry_run=true (nothing saved).
// The preview shows the server's answer per row; Import sends the same
// rebuilt file with the import_key to POST .../import/. The payroll cycle is
// set by the server from the Settings' payroll treatment.

const STEPS = ["Upload File", "Map Columns", "Preview & Import"];
const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT = ".xlsx,.xls,.csv";

// System fields = the template's columns (header names the API reads).
const IMPORT_FIELDS = [
  { key: "employee_code", header: "Employee Code", label: "Employee Code", required: true, aliases: ["employee code", "employee id", "emp code", "emp id", "emp no", "employee no", "staff id", "code"] },
  { key: "type", header: "Type", label: "Type", required: true, aliases: ["type", "reimbursement type", "expense type", "claim type", "category"] },
  { key: "bill_date", header: "Bill Date", label: "Bill Date", required: true, aliases: ["bill date", "date", "invoice date", "expense date", "receipt date"] },
  { key: "bill_number", header: "Bill Number", label: "Bill Number", required: false, aliases: ["bill number", "bill no", "invoice number", "invoice no", "receipt no", "receipt number"] },
  { key: "amount", header: "Amount", label: "Amount", required: true, aliases: ["amount", "amount rs", "claim amount", "value", "rupees"] },
  { key: "description", header: "Description", label: "Description", required: true, aliases: ["description", "purpose", "details", "remarks", "narration"] },
];

const fieldSx = {
  ...globalTextFieldSx,
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: designColors.teal },
  "& .MuiInputBase-input": { fontSize: 14 },
};
const tableHeadSx = { ...designShell.tableHeadCellBase, py: 1, whiteSpace: "nowrap" };
const cellSx = { ...sharedTableBodyCellSx, py: 1 };
const errorText = "#DC2626";

const formatBytes = (bytes) =>
  bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
const isBlank = (value) => value === null || value === undefined || String(value).trim() === "";
const text = (value) => (isBlank(value) ? "–" : String(value));
const normalize = (value) => String(value ?? "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

// Header row = the first non-blank row; data rows keep their order.
const readSheet = async (file) => {
  const book = XLSX.read(await file.arrayBuffer(), { type: "array", cellNF: true, raw: true });
  const sheet = convertDateTimeCells(book.Sheets[book.SheetNames[0]], XLSX.SSF);
  const all = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: "" });
  const blankRow = (row) => !row || row.every(isBlank);
  const headerIndex = all.findIndex((row) => !blankRow(row));
  if (headerIndex === -1) return { headers: [], rows: [] };
  const headers = all[headerIndex].map((cell) => String(cell ?? "").trim());
  const rows = all.slice(headerIndex + 1).filter((row) => !blankRow(row)).map((cells, index) => ({ rowNumber: index + 1, cells }));
  return { headers, rows };
};

// Columns matched by header name (exact alias first, then partial).
const autoMapColumns = (headers) => {
  const mapping = {};
  const used = new Set();
  const match = (test) => headers.forEach((header, index) => {
    if (mapping[index]) return;
    const name = normalize(header);
    const field = IMPORT_FIELDS.find((item) => !used.has(item.key) && test(name, item));
    if (field) { mapping[index] = field.key; used.add(field.key); }
  });
  match((name, field) => field.aliases.includes(name));
  match((name, field) => name && field.aliases.some((alias) => name.includes(alias)));
  return mapping;
};

const missingRequired = (mapping) => {
  const mapped = new Set(Object.values(mapping));
  return IMPORT_FIELDS.filter((field) => field.required && !mapped.has(field.key));
};

// Each data row as { rowNumber, raw: { [fieldKey]: value } }.
const mappedRows = (file, mapping) => file.rows.map(({ rowNumber, cells }) => {
  const raw = {};
  Object.entries(mapping).forEach(([index, key]) => { raw[key] = cells[Number(index)]; });
  return { rowNumber, raw };
});

// The rows rebuilt as an .xlsx with the template's headers, for the API.
const buildApiFile = (rows, name) => {
  const aoa = [IMPORT_FIELDS.map((field) => field.header), ...rows.map(({ raw }) => IMPORT_FIELDS.map((field) => raw[field.key] ?? ""))];
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, XLSX.utils.aoa_to_sheet(aoa), "Reimbursements");
  const bytes = XLSX.write(book, { type: "array", bookType: "xlsx" });
  const fileName = `${name.replace(/\.[^.]+$/, "")}.xlsx`;
  return new File([bytes], fileName, { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
};

const Card = ({ title, helper, action, children, sx }) => (
  <Mui.Box sx={{ ...detailCardSx, ...sx }}>
    {title ? (
      <Mui.Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1} sx={{ mb: 1.5 }}>
        <Mui.Box>
          <Mui.Typography sx={designType.sectionHeading}>{title}</Mui.Typography>
          {helper ? <Mui.Typography sx={{ ...designType.helperText, mt: 0.25 }}>{helper}</Mui.Typography> : null}
        </Mui.Box>
        {action}
      </Mui.Stack>
    ) : null}
    {children}
  </Mui.Box>
);

const FileCard = ({ file, onChange }) => (
  <Mui.Stack direction="row" spacing={1.5} alignItems="center" sx={{ ...detailCardSx, backgroundColor: designColors.slateBg }}>
    <Mui.Box sx={{ width: 40, height: 40, borderRadius: designRadii.iconButton, backgroundColor: "#E7F8EF", color: "#12B76A", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <DescriptionOutlined />
    </Mui.Box>
    <Mui.Box sx={{ minWidth: 0, flex: 1 }}>
      <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold }} noWrap>{file.name}</Mui.Typography>
      <Mui.Typography sx={designType.captionText}>{file.rows.length.toLocaleString("en-IN")} rows · {formatBytes(file.size)}</Mui.Typography>
    </Mui.Box>
    {onChange ? <SecondaryButton onClick={onChange} sx={{ minHeight: 36, minWidth: 0 }}>Change File</SecondaryButton> : null}
  </Mui.Stack>
);

const guidelinesFor = (types) => {
  const active = types.filter((type) => type.active);
  const needBill = active.filter((type) => type.billNeeded).map((type) => type.name);
  return [
    "Required columns: Employee Code, Type, Bill Date, Amount and Description. Bill Number is optional.",
    `Type must match a reimbursement type in Settings exactly${active.length ? `: ${active.map((type) => type.name).join(", ")}` : ""}.`,
    "Bill Date as DD-MM-YYYY or YYYY-MM-DD, not in the future and within the type's bill age.",
    "Amount in rupees, more than 0, within the type's per-claim and monthly limits.",
    needBill.length
      ? `Import has no bill files, so these types need a Bill Number: ${needBill.join(", ")}.`
      : "Import has no bill files. Types that need a bill need a Bill Number.",
    "The payroll cycle is set automatically from the payroll treatment in Settings. The same bill is never saved twice.",
  ];
};

// ---- Step 1: Upload ----

const UploadStep = ({ file, error, onFile, guidelines, onDownloadTemplate }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const pick = (files) => { if (files?.[0]) onFile(files[0]); };

  return (
    <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 3fr) minmax(0, 2fr)" }, gap: 1.5, alignItems: "stretch" }}>
      <Mui.Stack spacing={1.5} sx={{ minHeight: "100%" }}>
        <Mui.Box
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") inputRef.current?.click(); }}
          onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => { event.preventDefault(); setDragging(false); pick(event.dataTransfer.files); }}
          sx={{
            ...detailCardSx,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 6,
            px: 3,
            textAlign: "center",
            cursor: "pointer",
            borderStyle: "dashed",
            borderWidth: 2,
            borderColor: dragging ? designColors.teal : designColors.slate300,
            backgroundColor: dragging ? designColors.tealTint : designColors.white,
            "&:hover": { borderColor: designColors.teal },
          }}
        >
          <input ref={inputRef} hidden type="file" accept={ACCEPT} onChange={(event) => { pick(event.target.files); event.target.value = ""; }} />
          <FileUploadOutlined sx={{ fontSize: 40, color: designColors.teal }} />
          <Mui.Typography sx={{ ...designType.sectionHeading, fontSize: 16, mt: 1.5 }}>Drag and drop your file here</Mui.Typography>
          <Mui.Typography sx={{ ...designType.helperText, mt: 0.5 }}>
            or <Mui.Box component="span" sx={{ color: designColors.tealDark, fontWeight: designWeights.semibold }}>click to browse</Mui.Box>
          </Mui.Typography>
          <Mui.Typography sx={{ ...designType.captionText, mt: 2 }}>Supported formats: Excel (.xlsx, .xls), CSV (.csv) · Max 5 MB</Mui.Typography>
          <Mui.Button
            startIcon={<FileDownloadOutlined />}
            onClick={(event) => { event.stopPropagation(); onDownloadTemplate(); }}
            sx={{ mt: 2, textTransform: "none", fontWeight: designWeights.semibold, color: designColors.tealDark }}
          >
            Download Reimbursements Template
          </Mui.Button>
        </Mui.Box>
        {file ? <FileCard file={file} /> : null}
        {error ? <RequestInfoBanner tone="danger" title="Cannot use this file" body={error} /> : null}
      </Mui.Stack>

      <Mui.Stack spacing={1.5} sx={{ minHeight: "100%" }}>
        <RequestInfoBanner title="Template Information" body="Use the Reimbursements template to import claims for employees. It includes the columns and a sheet listing the company's active types." />
        <Card sx={{ flex: 1 }}>
          <Mui.Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <DescriptionOutlined sx={{ fontSize: 20, color: designColors.tealDark }} />
            <Mui.Typography sx={designType.sectionHeading}>Reimbursements Import Guidelines</Mui.Typography>
          </Mui.Stack>
          <Mui.Stack spacing={1}>
            {guidelines.map((line, index) => (
              <Mui.Stack key={line} direction="row" spacing={1.25} alignItems="flex-start">
                <Mui.Box sx={{ ...designType.captionText, flexShrink: 0, width: 20, height: 20, borderRadius: "50%", backgroundColor: designColors.slate100, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {index + 1}
                </Mui.Box>
                <Mui.Typography sx={designType.helperText}>{line}</Mui.Typography>
              </Mui.Stack>
            ))}
          </Mui.Stack>
        </Card>
      </Mui.Stack>
    </Mui.Box>
  );
};

// ---- Step 2: Map columns ----

const MapStep = ({ file, mapping, onMappingChange, onReset, onChangeFile }) => {
  const missing = missingRequired(mapping);
  const usedFields = new Set(Object.values(mapping));
  return (
    <Mui.Stack spacing={1.5}>
      <FileCard file={file} onChange={onChangeFile} />
      <Card
        title="Map File Columns"
        helper="Columns were matched automatically by header name — they can be in any order and renamed. Check each match and change it if needed."
        action={<Mui.Button startIcon={<Replay />} onClick={onReset} sx={{ textTransform: "none", fontWeight: designWeights.semibold, color: designColors.tealDark }}>Reset Mapping</Mui.Button>}
      >
        <Mui.TableContainer sx={{ border: `1px solid ${designColors.slate200}`, borderRadius: designRadii.card }}>
          <Mui.Table size="small">
            <Mui.TableHead>
              <Mui.TableRow>
                <Mui.TableCell sx={tableHeadSx}>File Column (from Excel)</Mui.TableCell>
                <Mui.TableCell sx={tableHeadSx}>Map to System Field</Mui.TableCell>
              </Mui.TableRow>
            </Mui.TableHead>
            <Mui.TableBody>
              {file.headers.map((header, index) => {
                const fieldKey = mapping[index] ?? "";
                return (
                  <Mui.TableRow key={`${header}-${index}`}>
                    <Mui.TableCell sx={{ ...cellSx, width: "45%" }}>{header || `Column ${index + 1}`}</Mui.TableCell>
                    <Mui.TableCell sx={cellSx}>
                      {/* A field already mapped to another column is left out. */}
                      <SelectField
                        value={fieldKey}
                        onChange={(value) => onMappingChange(index, value)}
                        options={[
                          { value: "", label: "Do not import" },
                          ...IMPORT_FIELDS.filter((option) => !usedFields.has(option.key) || option.key === fieldKey).map((option) => ({
                            value: option.key,
                            label: `${option.label}${option.required ? " *" : ""}`,
                          })),
                        ]}
                        placeholder="Do not import"
                      />
                    </Mui.TableCell>
                  </Mui.TableRow>
                );
              })}
            </Mui.TableBody>
          </Mui.Table>
        </Mui.TableContainer>
        {missing.length ? (
          <Mui.Box sx={{ mt: 2 }}>
            <RequestInfoBanner tone="warning" title="Required fields not mapped" body={`Map a column to: ${missing.map((item) => item.label).join(", ")}.`} />
          </Mui.Box>
        ) : null}
      </Card>
    </Mui.Stack>
  );
};

// ---- Step 3: Preview & Import ----

const PREVIEW_TABS = [
  { key: "all", label: "All Rows" },
  { key: "valid", label: "Valid Rows" },
  { key: "error", label: "Error Rows" },
];

const TabButtons = ({ tabs, tab, counts, onChange }) => (
  <Mui.Stack direction="row" spacing={1} sx={{ overflowX: "auto" }} role="tablist">
    {tabs.map((item) => {
      const active = item.key === tab;
      return (
        <Mui.ButtonBase
          key={item.key}
          role="tab"
          aria-selected={active}
          onClick={() => onChange(item.key)}
          sx={{ px: 1.5, py: 0.75, flexShrink: 0, borderRadius: designRadii.control, border: `1px solid ${active ? designColors.tealDark : designColors.slate200}`, backgroundColor: active ? designColors.tealTint : designColors.white }}
        >
          <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: active ? designWeights.semibold : designWeights.medium, color: active ? designColors.tealDark : designColors.slate700, whiteSpace: "nowrap" }}>
            {item.label} ({counts[item.key].toLocaleString("en-IN")})
          </Mui.Typography>
        </Mui.ButtonBase>
      );
    })}
  </Mui.Stack>
);

const usePaged = (items, resetKey) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  useEffect(() => setPage(0), [resetKey]);
  return {
    pageItems: items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    footer: (
      <Mui.Box sx={{ px: 2, py: 1, borderTop: `1px solid ${designColors.slate200}` }}>
        {paginationFooter({
          page,
          rowsPerPage,
          total: items.length,
          itemLabel: "rows",
          onPageChange: setPage,
          onRowsPerPageChange: (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); },
          rowsPerPageOptions,
        })}
      </Mui.Box>
    ),
  };
};

const PREVIEW_COLUMNS = ["Row", "Employee Code", "Employee", "Type", "Bill Date", "Bill Number", "Amount (₹)", "Description", "Status / Reason"];

const PreviewStep = ({ results, validAmount }) => {
  const [tab, setTab] = useState(results.some((row) => !row.ok) ? "error" : "all");
  const [query, setQuery] = useState("");
  const counts = { all: results.length, valid: results.filter((row) => row.ok).length, error: results.filter((row) => !row.ok).length };

  const shown = useMemo(() => {
    const base = tab === "valid" ? results.filter((row) => row.ok) : tab === "error" ? results.filter((row) => !row.ok) : results;
    const needle = query.trim().toLowerCase();
    return needle
      ? base.filter((row) => [...Object.values(row.raw), row.name, ...row.errors].some((value) => String(value ?? "").toLowerCase().includes(needle)))
      : base;
  }, [tab, query, results]);
  const { pageItems, footer } = usePaged(shown, `${tab}:${query}`);

  return (
    <Mui.Stack spacing={1.5}>
      <Card title="Summary" helper="Checked by the server with the same rules as Add Reimbursement. Nothing is saved yet.">
        <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" }, gap: 1.5 }}>
          {/* Cards pick the rows shown below, same as the tabs. */}
          <StatCard icon={StorageOutlined} title="Total Rows" value={counts.all.toLocaleString("en-IN")} accent="#12B76A" tint="#E7F8EF" onClick={() => setTab("all")} isActive={tab === "all"} activeBorder />
          <StatCard icon={CheckCircleOutline} title="Valid Rows" value={counts.valid.toLocaleString("en-IN")} accent={designColors.tealDark} tint={designColors.tealTint} onClick={() => setTab("valid")} isActive={tab === "valid"} activeBorder />
          <StatCard icon={CancelOutlined} title="Error Rows" value={counts.error.toLocaleString("en-IN")} accent={errorText} tint="#FEF2F2" onClick={() => setTab("error")} isActive={tab === "error"} activeBorder />
          <StatCard icon={CurrencyRupee} title="Valid Amount" value={`₹${money(validAmount)}`} accent="#2878FF" tint="#EAF2FF" />
        </Mui.Box>
        {counts.error ? (
          <Mui.Box sx={{ mt: 1.5 }}>
            <RequestInfoBanner
              tone="warning"
              body={`${counts.error.toLocaleString("en-IN")} row(s) have errors and will not be imported. Fix them in the file and upload it again, or import only the valid rows.`}
            />
          </Mui.Box>
        ) : null}
      </Card>

      <Mui.Box sx={{ ...detailCardSx, p: 0, overflow: "hidden" }}>
        <Mui.Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} sx={{ p: 2 }}>
          <TabButtons tabs={PREVIEW_TABS} tab={tab} counts={counts} onChange={setTab} />
          <Mui.TextField
            size="small"
            placeholder="Search..."
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            sx={{ ...fieldSx, width: { xs: "100%", md: 260 } }}
            InputProps={{ startAdornment: <Mui.InputAdornment position="start"><Search sx={{ fontSize: 18, color: designColors.slate400 }} /></Mui.InputAdornment> }}
          />
        </Mui.Stack>
        <Mui.TableContainer sx={{ overflowX: "auto" }}>
          <Mui.Table size="small">
            <Mui.TableHead>
              <Mui.TableRow>
                {PREVIEW_COLUMNS.map((label) => (
                  <Mui.TableCell key={label} sx={{ ...tableHeadSx, ...(label.startsWith("Amount") ? { textAlign: "right" } : {}) }}>{label}</Mui.TableCell>
                ))}
              </Mui.TableRow>
            </Mui.TableHead>
            <Mui.TableBody>
              {pageItems.length === 0 ? (
                <Mui.TableRow>
                  <Mui.TableCell colSpan={PREVIEW_COLUMNS.length} sx={{ ...cellSx, textAlign: "center", py: 4 }}>No rows to show.</Mui.TableCell>
                </Mui.TableRow>
              ) : (
                pageItems.map((row) => (
                  <Mui.TableRow key={row.rowNumber} sx={row.ok ? undefined : { backgroundColor: "#FEF2F2" }}>
                    <Mui.TableCell sx={cellSx}>{row.rowNumber}</Mui.TableCell>
                    <Mui.TableCell sx={cellSx}>{text(row.raw.employee_code)}</Mui.TableCell>
                    <Mui.TableCell sx={cellSx}>{text(row.name)}</Mui.TableCell>
                    <Mui.TableCell sx={cellSx}>{text(row.raw.type)}</Mui.TableCell>
                    <Mui.TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }}>{row.billDate ? formatDate(row.billDate) : text(row.raw.bill_date)}</Mui.TableCell>
                    <Mui.TableCell sx={cellSx}>{text(row.raw.bill_number)}</Mui.TableCell>
                    <Mui.TableCell sx={{ ...cellSx, textAlign: "right" }}>{row.amount != null && row.amount !== "" ? money(row.amount) : text(row.raw.amount)}</Mui.TableCell>
                    <Mui.TableCell sx={{ ...cellSx, whiteSpace: "normal", minWidth: 180 }}>{text(row.raw.description)}</Mui.TableCell>
                    <Mui.TableCell sx={{ ...cellSx, whiteSpace: "normal", minWidth: 200, fontWeight: row.ok ? designWeights.semibold : undefined, color: row.ok ? designColors.tealDark : errorText }}>
                      {row.ok ? "Valid" : row.errors.join(" ") || "Not valid"}
                    </Mui.TableCell>
                  </Mui.TableRow>
                ))
              )}
            </Mui.TableBody>
          </Mui.Table>
        </Mui.TableContainer>
        {footer}
      </Mui.Box>
    </Mui.Stack>
  );
};

// ---- Result ----

const ResultStep = ({ result, results }) => {
  const skippedRows = results.filter((row) => !row.ok);
  const { pageItems, footer } = usePaged(skippedRows, "skipped");
  return (
    <Mui.Stack spacing={1.5}>
      <Card title={result.skipped ? "Import Finished With Skipped Rows" : "Import Complete"} helper={result.message || "Claims saved with source Import. Their status follows Approval Required in Settings."}>
        <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" }, gap: 1.5 }}>
          <StatCard icon={StorageOutlined} title="Total Rows" value={results.length.toLocaleString("en-IN")} accent="#12B76A" tint="#E7F8EF" />
          <StatCard icon={CheckCircleOutline} title="Imported" value={result.created.toLocaleString("en-IN")} accent={designColors.tealDark} tint={designColors.tealTint} />
          <StatCard icon={CancelOutlined} title="Skipped" value={result.skipped.toLocaleString("en-IN")} accent={errorText} tint="#FEF2F2" />
          <StatCard icon={CurrencyRupee} title="Amount Imported" value={`₹${money(result.amount)}`} accent="#2878FF" tint="#EAF2FF" />
        </Mui.Box>
      </Card>
      {skippedRows.length ? (
        <Mui.Box sx={{ ...detailCardSx, p: 0, overflow: "hidden" }}>
          <Mui.Typography sx={{ ...designType.sectionHeading, p: 2 }}>Skipped Rows</Mui.Typography>
          <Mui.TableContainer sx={{ overflowX: "auto" }}>
            <Mui.Table size="small">
              <Mui.TableHead>
                <Mui.TableRow>
                  {["Row", "Employee Code", "Type", "Amount (₹)", "Reason"].map((label) => <Mui.TableCell key={label} sx={tableHeadSx}>{label}</Mui.TableCell>)}
                </Mui.TableRow>
              </Mui.TableHead>
              <Mui.TableBody>
                {pageItems.map((row) => (
                  <Mui.TableRow key={row.rowNumber}>
                    <Mui.TableCell sx={cellSx}>{row.rowNumber}</Mui.TableCell>
                    <Mui.TableCell sx={cellSx}>{text(row.raw.employee_code)}</Mui.TableCell>
                    <Mui.TableCell sx={cellSx}>{text(row.raw.type)}</Mui.TableCell>
                    <Mui.TableCell sx={cellSx}>{text(row.raw.amount)}</Mui.TableCell>
                    <Mui.TableCell sx={{ ...cellSx, whiteSpace: "normal", color: errorText }}>{row.errors.join(" ") || "Not valid"}</Mui.TableCell>
                  </Mui.TableRow>
                ))}
              </Mui.TableBody>
            </Mui.Table>
          </Mui.TableContainer>
          {footer}
        </Mui.Box>
      ) : null}
    </Mui.Stack>
  );
};

// ---- Wizard ----

// onImported(result) runs after the save so the list can reload; Done closes.
export const ReimbursementImport = ({ domain, companyId, types = [], onClose, onImported }) => {
  const history = useHistory();
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [mapping, setMapping] = useState({});
  // Server check: { importKey, validAmount, apiFile, results: [{ rowNumber, raw, ok, errors, name, billDate, amount }] }.
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [result, setResult] = useState(null);

  const handleFile = async (picked) => {
    setFileError("");
    const extension = picked.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(extension)) {
      setFile(null);
      setFileError("Unsupported file type. Upload an Excel (.xlsx, .xls) or CSV (.csv) file.");
      return;
    }
    if (picked.size > MAX_BYTES) {
      setFile(null);
      setFileError("The file is larger than 5 MB.");
      return;
    }
    try {
      const { headers, rows } = await readSheet(picked);
      if (!headers.length || !rows.length) {
        setFile(null);
        setFileError("The file has no data rows under the header row.");
        return;
      }
      setFile({ name: picked.name, size: picked.size, headers, rows });
      setMapping(autoMapColumns(headers));
      setCheck(null);
    } catch (error) {
      setFile(null);
      setFileError("This file could not be read. Use the template.");
    }
  };

  // Map Columns > Next: rebuild the file with the template headers and let
  // the server check every row (dry run).
  const handleMapNext = async () => {
    const rows = mappedRows(file, mapping);
    const apiFile = buildApiFile(rows, file.name);
    setChecking(true);
    setCheckError("");
    try {
      const answer = await checkPayInputImport({ domain, companyId, file: apiFile });
      // Server row 2 = first data row of the rebuilt file.
      const byRow = new Map(answer.rows.map((row) => [Number(row.row) - 1, row]));
      const results = rows.map(({ rowNumber, raw }) => {
        const server = byRow.get(rowNumber);
        return {
          rowNumber,
          raw,
          ok: Boolean(server?.ok),
          errors: server ? server.errors : ["Not checked by the server"],
          name: server?.name || "",
          billDate: server?.billDate || "",
          amount: server?.amount ?? null,
        };
      });
      setCheck({ importKey: answer.importKey, validAmount: answer.validAmount, apiFile, results });
      setStep(2);
    } catch (error) {
      setCheckError(error.message);
    } finally {
      setChecking(false);
    }
  };

  const validCount = check ? check.results.filter((row) => row.ok).length : 0;

  const runImport = async () => {
    if (saving || !validCount || !check?.importKey) return;
    setSaving(true);
    setSaveError("");
    try {
      const answer = await savePayInputImport({ domain, companyId, file: check.apiFile, importKey: check.importKey });
      setResult(answer);
      onImported?.(answer);
    } catch (error) {
      setSaveError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      await downloadPayInputTemplate({ domain, companyId });
    } catch (error) {
      showToast("error", error.message || "Couldn't download the template.");
    }
  };

  const canContinue = (step === 0 && Boolean(file)) || (step === 1 && missingRequired(mapping).length === 0);
  const close = () => { if (!saving && !checking) onClose(); };

  const footer = result ? (
    <Mui.Stack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}>
      <PrimaryButton onClick={onClose}>Done</PrimaryButton>
    </Mui.Stack>
  ) : (
    <Mui.Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} justifyContent="space-between" sx={{ width: "100%" }}>
      <SecondaryButton disabled={saving || checking} onClick={close}>Cancel</SecondaryButton>
      <Mui.Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5}>
        {step > 0 ? <SecondaryButton disabled={saving || checking} startIcon={<ArrowBack />} onClick={() => setStep(step - 1)}>Back</SecondaryButton> : null}
        {step < 2 ? (
          <PrimaryButton
            endIcon={<ArrowForward />}
            disabled={!canContinue || checking}
            loading={step === 1 && checking}
            onClick={step === 1 ? handleMapNext : () => setStep(step + 1)}
          >
            {step === 1 && checking ? "Validating..." : "Next"}
          </PrimaryButton>
        ) : (
          <Mui.Tooltip arrow placement="top" slotProps={tooltipStyleProps} title={!validCount ? "There are no valid rows to import." : ""}>
            <span>
              <PrimaryButton startIcon={<FileUploadOutlined />} disabled={!validCount || saving} loading={saving} onClick={runImport}>
                Import {validCount ? validCount.toLocaleString("en-IN") : ""}
              </PrimaryButton>
            </span>
          </Mui.Tooltip>
        )}
      </Mui.Stack>
    </Mui.Stack>
  );

  const subtitles = [
    "Upload a file to import reimbursement claims for employees.",
    "Map your file columns to the reimbursement fields.",
    "Review the checked rows and import.",
  ];

  return (
    <GlobalSidePanel
      open
      onClose={saving || checking ? undefined : onClose}
      expanded
      onToggleExpand={() => {}}
      hideExpand
      title="Import Reimbursements"
      subtitle={result ? "Import result." : subtitles[step]}
      // Payroll -> Payroll home; Reimbursements -> back to the list.
      breadcrumb={[
        { label: "Payroll", onClick: () => { if (!saving && !checking) history.push("/admin/payroll/setup"); } },
        "Pay Inputs",
        { label: "Reimbursements", onClick: close },
        "Import",
      ]}
      bodySx={{ backgroundColor: designColors.pageBg, p: { xs: 1.5, sm: 2 } }}
      footer={footer}
    >
      <Mui.Stack spacing={1.5}>
        {result ? (
          <ResultStep result={result} results={check?.results || []} />
        ) : (
          <>
            <Mui.Box sx={{ ...detailCardSx, py: 1 }}>
              <WizardStepper steps={STEPS} step={step} />
            </Mui.Box>
            {step === 0 ? (
              <UploadStep file={file} error={fileError} onFile={handleFile} guidelines={guidelinesFor(types)} onDownloadTemplate={downloadTemplate} />
            ) : step === 1 ? (
              <MapStep
                file={file}
                mapping={mapping}
                onMappingChange={(index, fieldKey) =>
                  setMapping((current) => {
                    const next = { ...current };
                    if (fieldKey) next[index] = fieldKey;
                    else delete next[index];
                    return next;
                  })
                }
                onReset={() => setMapping(autoMapColumns(file.headers))}
                onChangeFile={() => setStep(0)}
              />
            ) : (
              <PreviewStep results={check.results} validAmount={check.validAmount} />
            )}
          </>
        )}
        {checkError && !result ? <RequestInfoBanner tone="danger" title="Could not validate the rows" body={checkError} /> : null}
        {saveError ? <RequestInfoBanner tone="danger" title="Could not import reimbursements" body={saveError} /> : null}
      </Mui.Stack>
    </GlobalSidePanel>
  );
};
